import React, { useCallback, useEffect, useMemo, useState } from 'react';

import isEmpty from 'lodash/isEmpty';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import { cssMap, cx } from '@atlaskit/css';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { SORT_FIELD_KEY, SORT_ORDER } from '@atlassian/dynamic-cards-search-client';
import { ProductKeys } from '@atlassian/dynamic-cards-search-page';

import type { Filters } from '../../../../../types';

import { messages } from './messages';
import { SearchHubContainer } from './search-hub-container';

const styles = cssMap({
	header: {
		flexDirection: 'column',
		gap: token('space.200'),
	},
	footer: {
		justifyContent: 'space-between',
		width: '100%',
	},
});

const productFilter = {
	product: ProductKeys.Confluence,
};

export const hasFiltersOtherThanProductAndSortFilter = (filters: Filters = {}): boolean => {
	const { product, sortField, sortOrder, ...rest } = filters;

	return !isEmpty(rest);
};

const isSortDefault = ({ sortField, sortOrder }: { sortField?: string; sortOrder?: string }) => {
	const field = sortField || SORT_FIELD_KEY.LAST_MODIFIED_DATE;
	const order = sortOrder || SORT_ORDER.DESC;
	return field === SORT_FIELD_KEY.LAST_MODIFIED_DATE && order === SORT_ORDER.DESC;
};

type DynamicContentModalDialogProps = {
	closeModal: () => void;
	filters?: Filters;
	setFilters: (filters: Filters) => void;
};

export const DynamicContentModal = ({
	filters,
	setFilters,
	closeModal,
}: DynamicContentModalDialogProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [stagedFilters, setStagedFilters] = useState<Filters>({
		...productFilter,
		...filters,
	});

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'dynamicContentModalDialog',
			},
		}).fire();
	}, [createAnalyticsEvent]);

	const hasNoFilters = useMemo(() => {
		return (
			!hasFiltersOtherThanProductAndSortFilter(stagedFilters) &&
			isSortDefault({
				sortField: stagedFilters.sortField,
				sortOrder: stagedFilters.sortOrder,
			})
		);
	}, [stagedFilters]);

	const hasNoSearchFilters = useMemo(() => {
		return !hasFiltersOtherThanProductAndSortFilter(stagedFilters);
	}, [stagedFilters]);

	const handleSetStagedFilters = useCallback(
		(newFilters: Filters) => {
			setStagedFilters({
				...newFilters,
			});
		},
		[setStagedFilters],
	);

	const handleClearAllFilters = useCallback(() => {
		setStagedFilters({
			...productFilter,
		});
	}, [setStagedFilters]);

	const handleCancel = useCallback(() => {
		closeModal();
	}, [closeModal]);

	const handleConfirm = useCallback(() => {
		setFilters(stagedFilters);

		closeModal();

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'confirmButton',
				source: 'dynamicContentModalDialog',
				attributes: {
					filters: Object.keys(stagedFilters),
				},
			},
		}).fire();
	}, [stagedFilters, setFilters, closeModal, createAnalyticsEvent]);

	return (
		<Modal onClose={closeModal} width="x-large">
			<ModalHeader hasCloseButton>
				<Flex xcss={cx(styles.header)}>
					<ModalTitle>
						<FormattedMessage {...messages.header} />
					</ModalTitle>
					<Flex>
						<Text color="color.text.subtle">
							<FormattedMessage {...messages.headerSubtext} />
						</Text>
					</Flex>
				</Flex>
			</ModalHeader>
			<ModalBody>
				<SearchHubContainer filters={stagedFilters} setFilters={handleSetStagedFilters} />
			</ModalBody>
			<ModalFooter>
				<Flex xcss={cx(styles.footer)}>
					<Flex justifyContent="start">
						<Button appearance="subtle" onClick={handleClearAllFilters} isDisabled={hasNoFilters}>
							<FormattedMessage {...messages.clearAllFiltersButton} />
						</Button>
					</Flex>
					<Flex justifyContent="end" gap="space.100">
						<Button appearance="subtle" onClick={handleCancel}>
							<FormattedMessage {...messages.cancelButton} />
						</Button>
						<Button appearance="primary" onClick={handleConfirm} isDisabled={hasNoSearchFilters}>
							<FormattedMessage {...messages.confirmButton} />
						</Button>
					</Flex>
				</Flex>
			</ModalFooter>
		</Modal>
	);
};
